/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useMemo } from 'react';

import type {
  PromptRequiringResponseElement,
  ReviewResponseContainer,
} from 'bundles/compound-assessments/components/api/types/CompoundAssessmentsForm';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { FormPart } from 'bundles/compound-assessments/components/form-parts/FormPart';
import { typeNames } from 'bundles/compound-assessments/constants';
import { AssignmentRoles } from 'bundles/compound-assessments/types/Roles';
import type {
  QuestionRubricElement,
  QuestionRubricFormElementNode,
  QuestionRubricPrompt,
  QuestionRubricResponseContainer,
} from 'bundles/grading/api/types';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import { useAiSuggestionsContext } from 'bundles/grading/components/staff-graded-assignments/ai-suggestions/AiSuggestionsContext';
import { AutoSaveContext } from 'bundles/grading/components/staff-graded-assignments/auto-save/utils';
import { InlineFeedbackRubricContext } from 'bundles/grading/components/staff-graded-assignments/form-parts/InlineFeedbackRubricContext';
import type { RubricProps } from 'bundles/grading/components/staff-graded-assignments/form-parts/utils';
import { processNewPrompt, processNewResponseReviewPart } from 'bundles/grading/utils/formPartUtils';
import { getSubmitterUserId } from 'bundles/grading/utils/submissionUtils';

// override rubric input fields styles to match their appearance on the existing SGA Grading page.
const styles = {
  container: () => css`
    & > div {
      border: none;
      border-top: 1px solid var(--cds-color-grey-50);
    }

    input,
    textarea {
      font-size: 0.875rem;
      line-height: 1.5rem;
      display: block;
    }
  `,
};

const RubricShell: React.FC<RubricProps> = ({ rubric, isReadOnly = false }) => {
  const { pageData, submissionData } = useContext(SGAPageContext);
  const { localScopeId } = useContext(AutoSaveContext);
  const { isExpanded } = useContext(InlineFeedbackRubricContext);
  const { currentAiReviewPartSuggestion } = useAiSuggestionsContext();
  const formElementNode = rubric.formElementNode as QuestionRubricFormElementNode;
  const element = formElementNode.element as QuestionRubricElement;

  const responseId = element.responseId;

  // for inline feedback and box widget, the userId of a team member is required in order to fetch the file within box
  const currentSubmitterId = submissionData.data?.submitter ? getSubmitterUserId(submissionData.data?.submitter) : 0; // intentionally fail with 0 userId passed
  const props = useMemo(() => {
    const massagedPrompt = processNewPrompt(element.prompt as QuestionRubricPrompt);
    const responseReviewPart = processNewResponseReviewPart(element.response as QuestionRubricResponseContainer);
    return {
      formPart: {
        typeName: typeNames.PROMPT_REQUIRING_RESPONSE_ELEMENT,
        definition: {
          prompt: massagedPrompt,
          responseId,
          response: {
            typeName: typeNames.REVIEW_RESPONSE,
            definition: responseReviewPart,
          } as ReviewResponseContainer,
        } as PromptRequiringResponseElement,
      },
      isReadOnly,
      isGraderView: true,
      isDisabled: false,
      role: AssignmentRoles.GRADER,
      autoFocus: true,
      userId: currentSubmitterId,
      itemId: pageData.currentItemId,
      courseId: pageData.courseId,
      isExpanded,
    };
  }, [
    element.prompt,
    element.response,
    responseId,
    isReadOnly,
    currentSubmitterId,
    pageData.currentItemId,
    pageData.courseId,
    isExpanded,
  ]);

  // Provide unique `componentKey` to ensure nested components of FormPart rerender when we switch between rubrics of
  // the same type. This previously worked around an issue in FormPart where it would not react to prop changes, showing
  // responses for previous learners even though the props had changed to the next learner. Even though this specific
  // issue has since been fixed, other components under this tree rely on the ID not changing to function correctly,
  // such as auto-save (which will save responses to the wrong IDs if not remounted like we are doing here).
  const componentKey = `${responseId}~${localScopeId}`;
  return (
    <div className="pii-hide" css={styles.container}>
      <FormPart
        localScopeId={localScopeId}
        key={componentKey}
        isQuickGraderContext
        overrideChangeResponseAvailable={!!currentAiReviewPartSuggestion}
        {...props}
      />
    </div>
  );
};

export default RubricShell;
